/**
 * Grupos de aportantes (Mati, 2026-10-19): la empresa de varios fiduciantes, cada uno con su % fijo que suma 100. Un
 * aporte del grupo se reparte entre ellos por esos % (10.000 → A 2.500, B 4.000, C 3.500). Formulario del grupo, vista
 * previa del reparto y payloads. Mismos textos y redondeo que el backend (`app/grupos/reglas.py`): centavos con
 * `repartirJusto` y el que sobra al de mayor % (a igual %, al de menor id). Porcentajes escalados a 10^4: nunca float.
 */
import type { OpcionSelect } from "@/components";
import type { AporteGrupo, FiduciantePublico, Grupo } from "./api";
import { aPayloadAporte, validarAporte, type ErroresAporte, type FormAporte, type PayloadAporte } from "./aportes";
import { aCentavos, deCentavos, montoAInput, parseMonto, tipoCambioAInput } from "./gastos";
import { aEditable, aEscalado, aTextoApi, CIEN, formatEscalado, mensajeSuma } from "./participacion";
import { repartirJusto } from "./pedidos";

export const LARGO_NOMBRE_GRUPO = 80;

export interface LineaGrupo {
  fiduciante_id: string; // id como texto (Select); "" = sin elegir
  porcentaje: string;
}

export interface FormGrupo {
  nombre: string;
  lineas: LineaGrupo[];
}

export interface ErroresGrupo {
  nombre?: string;
  lineas?: string;
}

export interface PayloadGrupo {
  nombre: string;
  miembros?: { fiduciante_id: number; porcentaje: string }[];
}

const LINEA_VACIA: LineaGrupo = { fiduciante_id: "", porcentaje: "" };

export function formGrupo(g?: Grupo): FormGrupo {
  if (!g) return { nombre: "", lineas: [{ ...LINEA_VACIA }, { ...LINEA_VACIA }] };
  return {
    nombre: g.nombre,
    lineas: g.miembros.map((m) => ({
      fiduciante_id: String(m.fiduciante_id),
      porcentaje: aEditable(aEscalado(m.porcentaje) ?? 0),
    })),
  };
}

export const agregarLinea = (f: FormGrupo): FormGrupo => ({
  ...f,
  lineas: [...f.lineas, { ...LINEA_VACIA }],
});
export const quitarLinea = (f: FormGrupo, i: number): FormGrupo => ({
  ...f,
  lineas: f.lineas.filter((_, j) => j !== i),
});
export const cambiarLinea = (f: FormGrupo, i: number, campo: keyof LineaGrupo, valor: string): FormGrupo => ({
  ...f,
  lineas: f.lineas.map((l, j) => (j === i ? { ...l, [campo]: valor } : l)),
});

/** Suma de los % cargados (escalada); null si alguno no es un número válido. */
export function sumaGrupo(f: FormGrupo): number | null {
  let total = 0;
  for (const l of f.lineas) {
    if (l.porcentaje.trim() === "") continue;
    const e = aEscalado(l.porcentaje);
    if (e === null) return null;
    total += e;
  }
  return total;
}

/** "Suma: 100,0000%" o con lo que falta o sobra, para mostrar en vivo. */
export function textoSuma(f: FormGrupo): string {
  const total = sumaGrupo(f);
  return total === null ? "Revisá los %: hasta 3 enteros y 4 decimales" : mensajeSuma(total);
}

export function validarGrupo(f: FormGrupo): ErroresGrupo {
  const e: ErroresGrupo = {};
  const nombre = f.nombre.trim();
  if (!nombre) e.nombre = "Ingresá el nombre del grupo";
  else if (nombre.length > LARGO_NOMBRE_GRUPO) e.nombre = `Hasta ${LARGO_NOMBRE_GRUPO} caracteres`;
  const elegidas = f.lineas.filter((l) => l.fiduciante_id);
  const total = sumaGrupo(f);
  if (f.lineas.some((l) => !l.fiduciante_id)) e.lineas = "Elegí el fiduciante de cada fila (o quitá la fila)";
  else if (elegidas.length < 2) e.lineas = "Un grupo necesita al menos dos fiduciantes";
  else if (new Set(elegidas.map((l) => l.fiduciante_id)).size !== elegidas.length) e.lineas = "Un fiduciante está dos veces en el grupo";
  else if (f.lineas.some((l) => (aEscalado(l.porcentaje) ?? 0) <= 0)) e.lineas = "Cada fiduciante del grupo necesita un % mayor a cero";
  else if (total !== CIEN) {
    const t = total ?? 0;
    e.lineas = `Los % del grupo suman ${formatEscalado(t)}%: ${t < CIEN ? `faltan ${formatEscalado(CIEN - t)}` : `sobran ${formatEscalado(t - CIEN)}`}%`;
  }
  return e;
}

/** Con `conMiembros = false` (el grupo ya aportó) se manda sólo el nombre: los % no se cambian. */
export function aPayloadGrupo(f: FormGrupo, conMiembros = true): PayloadGrupo {
  const nombre = f.nombre.trim();
  if (!conMiembros) return { nombre };
  return {
    nombre,
    miembros: f.lineas.map((l) => ({
      fiduciante_id: Number(l.fiduciante_id),
      porcentaje: aTextoApi(aEscalado(l.porcentaje) ?? 0),
    })),
  };
}

/** Fiduciantes para la fila `i`: los que no están en otro grupo ni en otra fila (el de la fila, siempre). */
export function opcionesMiembro(fiduciantes: FiduciantePublico[], grupos: Grupo[], f: FormGrupo, i: number, grupoId: number | null): OpcionSelect[] {
  const enOtroGrupo = new Set(grupos.filter((g) => g.id !== grupoId).flatMap((g) => g.miembros.map((m) => String(m.fiduciante_id))));
  const enOtraFila = new Set(f.lineas.filter((_, j) => j !== i).map((l) => l.fiduciante_id));
  const propio = f.lineas[i]?.fiduciante_id;
  return fiduciantes
    .filter((x) => String(x.id) === propio || (!enOtroGrupo.has(String(x.id)) && !enOtraFila.has(String(x.id))))
    .map((x) => ({ valor: String(x.id), etiqueta: x.nombre }));
}

// ------------------------------------------------------------------ reparto de un aporte del grupo

export interface ParteVistaPrevia {
  fiduciante_id: number;
  nombre: string;
  porcentaje: string; // "25 %"
  monto: string; // decimal "2500.00"
}

/** "25.0000" → "25 %"; "33.3300" → "33,33 %" (como `etiqueta_porcentaje` del backend). */
export const etiquetaPorcentaje = (porcentaje: string): string => `${aEditable(aEscalado(porcentaje) ?? 0)} %`;

/** La parte de cada miembro, por % desc (a igual %, por id): el centavo que sobra va al primero. null sin monto válido. */
export function repartirGrupo(monto: string, grupo: Pick<Grupo, "miembros">): ParteVistaPrevia[] | null {
  const valor = parseMonto(monto);
  if (valor === null || grupo.miembros.length === 0) return null;
  const ordenados = [...grupo.miembros]
    .map((m) => ({ ...m, escalado: aEscalado(m.porcentaje) ?? 0 }))
    .sort((a, b) => b.escalado - a.escalado || a.fiduciante_id - b.fiduciante_id);
  const centavos = repartirJusto(
    aCentavos(valor),
    ordenados.map((m) => ({
      fiduciante_id: m.fiduciante_id,
      nombre: m.nombre,
      porcentaje: m.escalado,
    })),
  );
  return ordenados.map((m, i) => ({
    fiduciante_id: m.fiduciante_id,
    nombre: m.nombre,
    porcentaje: etiquetaPorcentaje(m.porcentaje),
    monto: deCentavos(centavos[i] ?? 0n),
  }));
}

/** Ninguna parte puede quedar en cero (el backend responde lo mismo). */
export const MENSAJE_MONTO_CHICO = "El monto es demasiado chico para repartirlo en el grupo";

export type ErroresAporteGrupo = ErroresAporte & { grupo_id?: string };

/** El formulario de aporte con "Un grupo": lo mismo que el de un fiduciante, con el grupo en vez del fiduciante. */
export function validarAporteGrupo(f: FormAporte, grupo: Grupo | null): ErroresAporteGrupo {
  const { fiduciante_id: _, ...e }: ErroresAporteGrupo = validarAporte({
    ...f,
    fiduciante_id: "grupo",
  });
  if (!grupo) return { ...e, grupo_id: "Elegí el grupo" };
  const partes = repartirGrupo(f.monto, grupo);
  if (!e.monto && partes?.some((p) => aCentavos(p.monto) <= 0n)) e.monto = MENSAJE_MONTO_CHICO;
  return e;
}

export type PayloadAporteGrupo = Omit<PayloadAporte, "fiduciante_id"> & {
  grupo_id?: number;
};

/** Sin `grupoId` (al editar) no se manda el grupo: no se cambia. */
export function aPayloadAporteGrupo(f: FormAporte, grupoId: number | null): PayloadAporteGrupo {
  const { fiduciante_id: _, ...resto } = aPayloadAporte(f);
  return grupoId === null ? resto : { grupo_id: grupoId, ...resto };
}

/** Para editar un aporte del grupo con el mismo formulario del aporte. */
export function formAporteGrupo(a: AporteGrupo): FormAporte {
  return {
    fiduciante_id: "",
    fecha: a.fecha,
    monto: montoAInput(a.monto),
    moneda: a.moneda,
    tipo_cambio: tipoCambioAInput(a.tipo_cambio),
    nota: a.nota ?? "",
    caja_id: a.caja_id === null ? "" : String(a.caja_id),
  };
}

export function opcionesGrupo(grupos: Grupo[]): OpcionSelect[] {
  return grupos.map((g) => ({ valor: String(g.id), etiqueta: g.nombre }));
}
